import { useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { downloadExport } from '../components/ExportMenu';
import { ClipboardList, Upload, Loader2 } from 'lucide-react';

// Tanggal hari ini dalam WITA (UTC+8) sebagai YYYY-MM-DD — sama dengan zona
// yang dipakai server untuk menafsirkan rentang tanggal.
function witaDate(offsetDays = 0) {
  const d = new Date(Date.now() + 8 * 3600 * 1000 + offsetDays * 86400 * 1000);
  return d.toISOString().slice(0, 10);
}

const JENIS = [
  { value: 'all', label: 'Semua transaksi' },
  { value: 'supplier', label: 'Invoice Supplier (PO → Invoice → Bank)' },
  { value: 'internal', label: 'Internal Payment Request (IPR)' },
  { value: 'operasional', label: 'Operasional Payment Request (OPR)' },
  { value: 'dp', label: 'Down Payment' },
];

export default function AuditLog() {
  const [dateFrom, setDateFrom] = useState(witaDate(-30));
  const [dateTo, setDateTo] = useState(witaDate(0));
  const [jenis, setJenis] = useState('all');
  const [format, setFormat] = useState('xlsx');
  const [sheet, setSheet] = useState('perjalanan');
  const [busy, setBusy] = useState(false);

  const invalid = !dateFrom || !dateTo || dateFrom > dateTo;

  const download = async () => {
    setBusy(true);
    const params = { format, date_from: dateFrom, date_to: dateTo, jenis };
    if (format === 'csv') params.sheet = sheet;
    await downloadExport('/admin/audit-log/export', params, `audit_log.${format}`);
    setBusy(false);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-log-page">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-slate-700" />
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Audit Log</h1>
            <p className="text-slate-500">Jejak waktu & user di setiap tahap transaksi sampai diproses dan di-export ke bank</p>
          </div>
        </div>

        <Card className="max-w-3xl">
          <CardHeader>
            <CardTitle className="text-lg">Export Audit Log</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Dari tanggal (mulai transaksi)</Label>
                <Input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from" />
              </div>
              <div className="space-y-1">
                <Label>Sampai tanggal</Label>
                <Input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to" />
              </div>
              <div className="space-y-1">
                <Label>Jenis transaksi</Label>
                <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={jenis} onChange={(e) => setJenis(e.target.value)} data-testid="audit-jenis">
                  {JENIS.map((j) => <option key={j.value} value={j.value}>{j.label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Format</Label>
                <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="audit-format">
                  <option value="xlsx">Excel (.xlsx) — 2 sheet</option>
                  <option value="csv">CSV (.csv) — 1 sheet</option>
                </select>
              </div>
              {format === 'csv' && (
                <div className="space-y-1 md:col-span-2">
                  <Label>Isi CSV</Label>
                  <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm" value={sheet} onChange={(e) => setSheet(e.target.value)} data-testid="audit-sheet">
                    <option value="perjalanan">Perjalanan Transaksi</option>
                    <option value="riwayat">Riwayat Status</option>
                  </select>
                </div>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 border p-4 text-sm text-slate-600 space-y-2">
              <p><strong>Perjalanan Transaksi</strong> — satu baris per transaksi: waktu & nama user saat PO dibuat, disubmit, barang diterima (TTB), invoice dibuat, dokumen dikirim clerk, diterima AP, tukar faktur / siap proses (atau disetujui untuk IPR/OPR, perusahaan diisi untuk OPR), pilih bank, export bank pertama & terakhir, dan upload bukti — plus durasi antar tahap (hari).</p>
              <p><strong>Riwayat Status</strong> — setiap perubahan status termasuk revert, retur, dan penolakan, beserta alasannya.</p>
              <p>Semua waktu dalam <strong>WITA</strong>. Rentang maksimal 1 tahun.</p>
            </div>

            <Button onClick={download} disabled={busy || invalid} className="bg-slate-800 hover:bg-slate-900" data-testid="audit-download">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Export Audit Log
            </Button>
            {invalid && <p className="text-xs text-red-600">Tanggal mulai tidak boleh melewati tanggal akhir.</p>}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
